import Vue from 'vue'

// First entry is the reference language the others are checked against
export const LANGS = ['id', 'en']
const DEFAULT_LANG = 'id'
const STORAGE_KEY = 'lang'

function initialLang() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (LANGS.includes(saved)) return saved
  } catch (e) {
    // Storage can be blocked (private mode, strict settings); fall back to the browser language
  }
  const browser = (navigator.language || '').slice(0, 2).toLowerCase()
  return LANGS.includes(browser) ? browser : DEFAULT_LANG
}

const state = Vue.observable({ lang: initialLang() })
document.documentElement.lang = state.lang

function applyLang(lang) {
  state.lang = lang
  document.documentElement.lang = lang
  try {
    localStorage.setItem(STORAGE_KEY, lang)
  } catch (e) {
    // Choice still applies for this visit, it just is not remembered
  }
}

// Must match the .lang-fade transitions in App.vue and Header.vue
const FADE_MS = 150
let targetLang = state.lang
let fadeTimer = null

// Fades the page text out, swaps the language, then fades it back in.
// `.lang-fade` turns the transitions on; `.lang-fade--out` hides the text.
export function setLang(lang) {
  if (!LANGS.includes(lang) || lang === targetLang) return
  targetLang = lang

  const root = document.documentElement
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    applyLang(lang)
    return
  }

  // A second click mid-fade restarts the sequence with the latest choice
  clearTimeout(fadeTimer)
  root.classList.add('lang-fade', 'lang-fade--out')
  fadeTimer = setTimeout(() => {
    applyLang(targetLang)
    // Vue patches the DOM in a microtask, before the next paint, so the new text is what fades in
    root.classList.remove('lang-fade--out')
    fadeTimer = setTimeout(() => root.classList.remove('lang-fade'), FADE_MS)
  }, FADE_MS)
}

// Read-only and reactive, like $route: anything that reads this.$lang re-renders when it changes
Object.defineProperty(Vue.prototype, '$lang', {
  configurable: true,
  get: () => state.lang,
})

// Wraps a page's copy ({ id: {...}, en: {...} }). In development, warns when a
// translation is missing a key or an array has a different length than Indonesian.
export function defineCopy(name, copy) {
  if (process.env.NODE_ENV !== 'production') {
    const [base, ...others] = LANGS
    others.forEach(lang => diffShape(copy[base], copy[lang], `${name}.${lang}`))
  }
  return copy
}

function diffShape(base, other, path) {
  const warn = problem => console.warn(`[i18n] ${path}: ${problem}`)

  if (Array.isArray(base)) {
    if (!Array.isArray(other)) return warn('expected an array')
    if (other.length !== base.length) return warn(`expected ${base.length} items, found ${other.length}`)
    base.forEach((item, i) => diffShape(item, other[i], `${path}[${i}]`))
  } else if (base && typeof base === 'object') {
    if (!other || typeof other !== 'object' || Array.isArray(other)) return warn('expected an object')
    Object.keys(base).forEach(key => {
      if (key in other) diffShape(base[key], other[key], `${path}.${key}`)
      else warn(`missing "${key}"`)
    })
    Object.keys(other).forEach(key => {
      if (!(key in base)) warn(`unexpected "${key}"`)
    })
  } else if (typeof other !== typeof base) {
    warn(`expected a ${typeof base}`)
  }
}

// Merges translated text into structural items (icons, flags) by each item's key
export function localize(items, texts) {
  return items.map(item => ({ ...item, ...texts[item.key] }))
}
